import React, { useState, useEffect } from 'react';
import { FiStar } from 'react-icons/fi';
import { useSurvey } from '@/hooks/useSurvey';
import { Modal } from '@/components/custom/modal/modal';
import SurveyAnswer from '@/components/custom/survey/SurveyAnswer';
// import MyProgressWidget from './MyProgressWidget';
import { t } from 'i18next';
import { WarningWallSlider, WarningWall } from '@/components/custom/warning-wall';
import { useClient } from 'jsonapi-react';
import { Loader } from '@/components/custom/loader';
import moment from 'moment';
import Breadcrumbs from '@/components/custom/breadcrumbs/breadcrumbs';
import { useSession } from '@/hooks/useSession';
import { DashboardDisclaimer } from './dashboard-disclaimer';
import DashboardStudyGuideCard from './dashboard-study-guide-card';
import DashboardShortcutCard from './dashboard-shortcut-card';
import roleta from '@/assets/images/Roleta.png';
import monitor from '@/assets/images/Monitor.png';
import { Link } from 'react-router-dom';
import DashboardCourseCard from './dashboard-course-card';
import toast from 'react-hot-toast';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';
import { DashboardShortcutCardWrapper } from '@/components/custom/dashboard';
import { BannerInfo, BannerInfoContent, BannerInfoIcon, BannerInfoTitle, BannerInfoDescription } from '@/components/custom/banner';
import { Button } from '@/components/ui/button';
import { MyProgress, MyProgressHeader, MyProgressTitle } from '@/components/custom/my-progress';
import { DashboardEmptyState, hasDashboardContent } from './dashboard-empty-state';

export function DashboardStudent() {
  const client = useClient();
  const { getStudentSurvey, currentStudentSurvey } = useSurvey();
  const { session } = useSession();
  const [alerts, setAlerts] = useState([]);
  const [lastStudyPlan, setLastStudyPlan] = useState();
  const [banners, setBanners] = useState();
  const [loading, setLoading] = useState(true);
  const [showSurvey, setShowSurvey] = useState();
  const [lastChallenge, setLastChallenge] = useState();
  const [liveClassroom, setLiveClassroom] = useState();
  const [lastCourseWatched, setLastCourseWatched] = useState(null);
  const { abilities } = usePlatformAbilities();

  const fetchData = () => {
    setLoading(true);
    const fetchBanners = client.fetch(`/bulletin_boards/active`);
    const fetchAlerts = client.fetch(`/dashboards/alerts`);
    const fetchCourse = client.fetch(`/courses/latest`);
    const fetchChallenge = client.fetch(`/challenges/latest`);
    const fetchLive = client.fetch(`/live_classrooms/next`);
    const fetchStudyPlan = client.fetch(`/study_plan_videos/current`);

    let promises = [fetchBanners, fetchAlerts, fetchCourse];

    promises = [...promises, ...(abilities?.['desafio-da-semana'] ? [fetchChallenge] : []), ...(abilities?.['aulas-ao-vivo'] ? [fetchLive] : []), ...(abilities?.['plano-de-aula'] ? [fetchStudyPlan] : [])];

    Promise.allSettled(promises).then(([bannersResponse, alertsResponse, courseResponse, challengeResponse, liveResponse, studyPlanResponse]) => {
      if (bannersResponse?.value?.error) {
        toast.error('Erro ao buscar banners');
      } else {
        setBanners(bannersResponse?.value?.data);
      }
      if (alertsResponse?.value?.error) {
        toast.error('Erro ao buscar alertas');
      } else {
        setAlerts(alertsResponse?.value?.data?.data);
      }
      if (courseResponse?.value?.error) {
        toast.error('Erro ao buscar último curso acessado');
      } else {
        setLastCourseWatched(courseResponse?.value?.data);
      }
      if (abilities?.['desafio-da-semana'] && challengeResponse?.value?.error) {
        toast.error('Erro ao buscar desafio');
      } else {
        setLastChallenge(challengeResponse?.value?.data);
      }
      if (abilities?.['aulas-ao-vivo'] && liveResponse?.value?.error) {
        toast.error('Erro ao buscar aula ao vivo');
      } else {
        setLiveClassroom(liveResponse?.value?.data);
      }
      if (abilities?.['plano-de-aula'] && studyPlanResponse?.value?.error) {
        toast.error('Erro ao buscar guia de estudo');
      } else {
        const message = 'No study plan videos available for today';
        if (studyPlanResponse?.value?.message && studyPlanResponse?.value?.message === message) {
          setLastStudyPlan(null);
        } else {
          setLastStudyPlan(studyPlanResponse?.value?.data);
        }
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    if (abilities) {
      fetchData();
    }
  }, [abilities]);

  const onCloseSurvey = () => {
    setShowSurvey(false);
    getStudentSurvey();
  };

  const breadCrumbs = {
    title: 'Início',
    nav: [
      {
        route: '/',
        name: `Olá, ${session?.user?.name}`,
        isActive: true
      }
    ]
  };

  const dataCourse = {
    id: lastCourseWatched?.['course_id'],
    title: lastCourseWatched?.['course_title'],
    'finished-learning-system-items': lastCourseWatched?.['finished_items_count'],
    'learning-system-item-count': lastCourseWatched?.['total_items_count'],
    progress: lastCourseWatched?.['course_percent'],
    image: { url: lastCourseWatched?.image?.url }
  };

  return (
    <>
      <Breadcrumbs data={breadCrumbs} />

      {loading ? (
        <Loader />
      ) : banners?.length > 1 && !loading ? (
        <WarningWallSlider data={banners} />
      ) : (
        !loading && (
          <WarningWall
            warning={banners?.[0]}
            key={banners?.[0]?.id}
          />
        )
      )}

      <DashboardDisclaimer alerts={alerts} />

      <DashboardStudyGuideCard
        studyPlan={lastStudyPlan?.video}
        id={lastStudyPlan?.id}
      />

      {(lastChallenge || liveClassroom) && (
        <DashboardShortcutCardWrapper>
          {lastChallenge && (
            <DashboardShortcutCard
              source={roleta}
              type="Desafio da semana"
              title={lastChallenge?.title}
              link="/desafio-da-semana/desafios"
              text={lastChallenge?.taxonomy?.name}
            />
          )}

          {liveClassroom && (
            <DashboardShortcutCard
              source={monitor}
              type="Aula ao vivo"
              title={liveClassroom?.title}
              link={`/aulas-ao-vivo/${liveClassroom?.id}`}
              text={`${moment(liveClassroom?.['initial-date']).format('DD/MM/YYYY')} às ${moment(liveClassroom?.['initial-date']).format('HH:mm')}`}
            />
          )}
        </DashboardShortcutCardWrapper>
      )}

      {currentStudentSurvey && (
        <BannerInfo className="mb-6">
          <BannerInfoContent>
            <BannerInfoIcon>
              <FiStar />
            </BannerInfoIcon>
            <BannerInfoTitle>{t('nps.newQuiz')}</BannerInfoTitle>
            <BannerInfoDescription>Queremos saber a sua opinião</BannerInfoDescription>
          </BannerInfoContent>

          <Button
            variant="outline"
            onClick={() => setShowSurvey(true)}
          >
            Responder
          </Button>
        </BannerInfo>
      )}

      {lastCourseWatched && Object.keys(lastCourseWatched).length > 0 && (
        <MyProgress>
          <MyProgressHeader>
            <MyProgressTitle>Último curso acessado</MyProgressTitle>

            <Button
              variant="link"
              asChild
            >
              <Link to="/curso">Acesse cursos</Link>
            </Button>
          </MyProgressHeader>

          <DashboardCourseCard course={dataCourse} />
        </MyProgress>
      )}

      {/* Aluno sem nenhuma seção de conteúdo (novo na plataforma ou nada
          liberado ainda): sem isto, o miolo da página fica completamente
          em branco abaixo do banner. */}
      {!loading && !hasDashboardContent({ lastStudyPlan, lastChallenge, liveClassroom, lastCourseWatched }) && <DashboardEmptyState />}

      {/* <MyProgressWidget /> */}

      <Modal
        show={showSurvey}
        onClose={() => setShowSurvey(false)}
        contentClassName="modal__content--p-md"
      >
        {currentStudentSurvey && (
          <SurveyAnswer
            studentSurvey={currentStudentSurvey}
            onClose={onCloseSurvey}
          />
        )}
      </Modal>
    </>
  );
}
